/**
 * Wire shapes for /api/v2/admin/appointments (cycle-24).
 *
 * BE projectAppointmentList hydrates the legacy APPOINTMENTS_FRAGMENT:
 *   dates / doctor{id,first_name,last_name,avatar} /
 *   patient{...,email} / plan{id,name,price,type,duration} /
 *   appointment_translators[{translator_plan, translator}]
 * doctor / patient / translator first_name + last_name are @Lang()
 * locale-picked to strings; plan.name same.
 *
 * Phase E Cycle 1 — status / state / creator / sender are now
 * canonical BE enum strings (see below).
 */

import { PlanType } from './doctorPlans';
import { Gender } from './users';

// Phase E Cycle 1 canonical enums (BE appointment.constants).
export enum AppointmentStatus {
  AWAITING_CONFIRMATION = 'AWAITING_CONFIRMATION',
  APPROVED = 'APPROVED',
  STARTED = 'STARTED',
  COMPLETED = 'COMPLETED',
  CANCELED = 'CANCELED',
  REJECTED = 'REJECTED',
  FINISH = 'FINISH'
}

export enum AppointmentState {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  MODERATE = 'MODERATE'
}

// Also used for the `sender` column on appointment_dates /
// appointment_files / appointment_notes rows.
export enum AppointmentCreator {
  Admin = 'admin',
  Patient = 'patient',
  Doctor = 'doctor'
}

export interface AppointmentDateRow {
  id: number;
  date: string;
  sender: AppointmentCreator | null;
  accepted: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface AppointmentPersonRef {
  id: number;
  first_name: string;
  last_name: string;
  avatar: string | null;
  email?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface AppointmentPlanRef {
  id: number;
  name: string;
  price: number | string;
  type: PlanType;
  duration: number | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface AppointmentTranslatorPlanRef {
  id: number;
  price: number | string;
  lang_1: string;
  lang_2: string;
}

export interface AppointmentTranslatorRef {
  id: number;
  translator_plan: AppointmentTranslatorPlanRef | null;
  translator: AppointmentPersonRef | null;
}

export interface AppointmentListRow {
  id: number;
  hashId?: string;
  status: AppointmentStatus | null;
  state: AppointmentState | null;
  created_at: string;
  price: number | string | null;
  paid: boolean;
  admin_paid: boolean;
  dates: AppointmentDateRow[];
  doctor: AppointmentPersonRef | null;
  patient: AppointmentPersonRef | null;
  plan: AppointmentPlanRef | null;
  appointment_translators: AppointmentTranslatorRef[];
  translator: string | number | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface AppointmentListResponse {
  results: AppointmentListRow[];
  total: number;
}

export interface AppointmentNote {
  id: number;
  note: string;
  sender: AppointmentCreator | null;
  created_at: string;
}

export interface AppointmentFile {
  id: number;
  file: string;
  sender: AppointmentCreator | null;
  created_at: string;
  appointment_id?: number;
}

export interface AppointmentAdminNote {
  id: number;
  note: string;
  created_at: string;
}

export interface AppointmentPaymentRow {
  id: number;
  note: string | null;
  paid: boolean;
  transfer: number | null;
  payed_at: string | null;
  file: string | null;
}

export interface AppointmentReport {
  id: number;
  report: string | null;
  date_report: string | null;
}

export interface AppointmentReportFile {
  id: number;
  file: string;
  created_at: string;
}

export interface AppointmentPatientPaymentInfo {
  id: number;
  appointment_id?: number;
  payment_info: Record<string, unknown> | string | null;
  reason: string | null;
  source: string | null;
  created_at: string;
  updated_at?: string;
}

export interface AppointmentPaymentInfoResult {
  id: number;
  patient_payment_info: AppointmentPatientPaymentInfo[];
}

export interface AppointmentDetailPerson extends AppointmentPersonRef {
  date_of_birth: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  languages: any;
  gender: Gender | null;
}

export interface AppointmentDetailPlan extends AppointmentPlanRef {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  description: Record<string, string> | string | null;
  features: string | null;
}

export interface AppointmentDetail extends AppointmentListRow {
  reason: string | null;
  creator: AppointmentCreator | null;
  links: string[] | null;
  admin_notes: AppointmentAdminNote[];
  admin_payment: AppointmentPaymentRow | null;
  patient_payment_info: AppointmentPatientPaymentInfo[];
  report: AppointmentReport | null;
  reports_file: AppointmentReportFile | null;
  files: AppointmentFile[];
  notes: AppointmentNote[];
  patient: AppointmentDetailPerson | null;
  doctor: AppointmentDetailPerson | null;
  plan: AppointmentDetailPlan | null;
}

export interface AppointmentNoteInput {
  note: string;
  sender?: AppointmentCreator;
}

export interface AppointmentFileInput {
  file: string;
  sender?: AppointmentCreator;
}

export interface AppointmentDateInput {
  date: string;
  sender?: AppointmentCreator;
}

export interface AppointmentRejectInput {
  reason?: string;
}

export interface AppointmentDoctorPaymentInput {
  paid: boolean;
  transfer: number;
  payed_at: string;
  note?: string;
  file?: string;
}

export interface OpenRoomResult {
  meetUrl: string;
  redirect_url: string;
}

export interface CreateAppointmentInput {
  patient_id?: number;
  doctor_id?: number;
  plan_id?: number;
  translator_plan_id?: number;
  paid?: boolean;
  date?: string;
  notes?: string;
  files?: string[];
  links?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export const PATIENT_TYPE = {
  platform_client_online: 'platform_client_online',
  private_client_online: 'private_client_online',
  platform_client_medical_tourism: 'platform_client_medical_tourism',
  private_client_medical_tourism: 'private_client_medical_tourism'
} as const;

export type PatientPaymentType =
  (typeof PATIENT_TYPE)[keyof typeof PATIENT_TYPE];

export const PATIENT_TYPE_VALUES = Object.values(
  PATIENT_TYPE
) as PatientPaymentType[];

export interface AdminPaymentAppointmentInput {
  price: number | string;
  reason?: string;
  notes?: string;
  doctor_id?: number;
  patient_id?: number;
  plan_id?: number;
  patientPaymentType?: PatientPaymentType;
}

export interface AppointmentListQuery {
  page?: number;
  limit?: number;
  status?: AppointmentStatus;
  name?: string;
  plan_type?: number;
  admin_paid?: boolean;
  doctor_id?: number;
  patient_id?: number;
  creator?: AppointmentCreator;
  paid?: boolean;
}
